import pinia from "@/plugins/PiniaPlugin";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";

import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    defaultTimeoutInMilliseconds,
    goFetch
} from "@/utils/network-utils";
import type { ApplicationIncidentPayload } from "@/types/api/fetch";
import type { DataSyncEvent } from "@/utils/data-sync-event-factory";
import type { UserProfileDataApiResponse } from "@/types/api/user";
import { Forbidden, InternalError, NotFound, Unauthorized } from "@/types/api/responses";
import { MessageTypes } from "@/types/enums/message-types";
import { ConfigurationError, Unknown } from "@/types/errors";
import { SignInPath } from "@/constants";

export function notifyAppIncident( name: string, msg: object, safeCtx: object ): void {
    goFetch< void, ApplicationIncidentPayload >( {
        endpoint: `ux/user/appincident`,
        payload: {
            relatedActionName: name,
            message: JSON.stringify( msg ),
            safeActionContext: safeCtx
        }
    } ).then( () => {
        /* NO-OP */
    } ).catch( ( err: any ) => logger.warn( JSON.stringify( err.message ) ) );
}

interface IStateService {
    handleClientError(): void;
    queueClientError( err: any, ctx: any ): void;
    isFaulted(): boolean;
}

type ApplicationError = {
    stackTrace: string | undefined,
    ctx: any,
    message?: string,
    status?: number,
    response?: string
}

enum StateMachine {
    Uninitialized = 0,
    Unauthenticated = 1,
    Faulted = 3
}

class ClientStateService implements IStateService {
    readonly #errors = new Array< ApplicationError >();

    #state: StateMachine = StateMachine.Uninitialized;

    constructor() {
        this.clearState();
    }

    private clearState(): void {
        // Remove all elements currently in the errors collection
        this.#errors.splice( 0 );
        // Set the FSM to the baseline state "unauthenticated"
        this.#state = StateMachine.Unauthenticated;
    }

    public isFaulted(): boolean {
        return this.#state === StateMachine.Faulted;
    }

    private _postMessage( message: DataSyncEvent ) {
        if ( typeof window !== 'undefined' ) {
            window.postMessage( message, `${ import.meta.env.VITE_ORIGIN }` );
        } else {
            postMessage( message );
        }
    }

    public handleClientError() {
        this.#errors.forEach( ( err: ApplicationError ) => {
            if ( Unauthorized.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.UNAUTHORIZED
                } );
            } else if ( Forbidden.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.FORBIDDEN
                } );
            } else if ( InternalError.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.INTERNAL_ERROR
                } );
            } else if ( NotFound.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.NOT_FOUND
                } );
            }  else if ( ConfigurationError.statusCode === err.status ) {
                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.CONFIGURATION,
                    payload: err?.message
                } );
            } else if ( Unknown.statusCode === err.status ) {
                logger.warn( err.stackTrace! );

                this._postMessage( {
                    typeSignifier: 'tmi_error',
                    eventType: MessageTypes.UNKNOWN,
                    payload: err.stackTrace
                } );
            }

            // TODO: track errors in real time
            const monitorUri = import.meta.env.VITE_CLIENT_MONITOR_URI;
            if ( !!monitorUri ) {
                // This should NOT impact the client's performance
                this.reportErrorsToMonitoringService().then();
            }
        } );

        this.clearState();
    }

    public queueClientError( err: any, ctx: any ) {
        let message = err.message;
        let status = Unknown.statusCode;
        let isCriticalError = false;

        if ( err instanceof Unauthorized ) {
            status = Unauthorized.statusCode;
            // Unauthorized means session is expired - trigger logout
            // This will clear all state and redirect to sign-in page
            logger.warn( 'Session expired - logging out user' );
            const authStore = useAuthStore( pinia );
            authStore.logout().then( () => {
                // After logout, redirect to sign-in page if not already there
                if ( window.location.pathname !== SignInPath ) {
                    window.location.href = SignInPath;
                }
            } );
        } else if ( err instanceof Forbidden ) {
            status = Forbidden.statusCode;
            isCriticalError = true; // Permission errors should fault the client - indicates access violation
        } else if ( err instanceof InternalError ) {
            status = InternalError.statusCode;
            // Internal errors logged but don't fault - could be transient
        } else if ( err instanceof NotFound ) {
            status = NotFound.statusCode;
            // Not found errors logged but don't fault - could be expected
        } else if ( err instanceof ConfigurationError ) {
            status = ConfigurationError.statusCode;
            isCriticalError = true; // Config errors should fault the client
        } else {
            logger.warn( JSON.stringify( err ) );
        }

        this.#errors.push( {
            stackTrace: new Error().stack,
            ctx: ctx,
            status: status,
            message: message
        } );

        // Only set state to Faulted for critical errors (permissions, config)
        // Unauthorized does NOT fault - allows user to re-authenticate
        // Other errors are logged but don't block all API calls
        if ( isCriticalError ) {
            this.#state = StateMachine.Faulted;
        }

        this.handleClientError();
    }

    private async reportErrorsToMonitoringService(): Promise< void > {
        const headers = buildStandardHeaders();

        this.#errors.forEach( ( appError: ApplicationError ) => {
            // Create an AbortController to short-circuit an API call that is taking too long
            const monitor = new AbortController();
            const timeoutId: NodeJS.Timeout = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), defaultTimeoutInMilliseconds );

            fetch( `${ import.meta.env.VITE_CLIENT_MONITOR_URI }`, {
                method: 'POST',
                body: JSON.stringify( appError ),
                headers: headers,
                signal: monitor.signal
            } ).catch( ( err: any ) => {
                logger.error( JSON.stringify( err.message ) );
            } ).finally( () => {
                // As a generally good practice, always clean up any resources that may or may not be active
                clearTimeout( timeoutId );
            });
        } );

        this.clearState();
    }
}

export const clientStateManager: IStateService = new ClientStateService();
